import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";

/**
 * Audio-to-MIDI models served from Modal. Each has its own route so each is its
 * own capability, but all three take the same request and return the same
 * shape, so one client service and one component cover them.
 *
 * Basic Pitch, the in-browser model, is not here: it never leaves the client.
 */

export const kMuscriptorRoute = "/muscriptor";
export const kSheetSage2Route = "/sheetsage2";
export const kDrumLoopTranscriptionRoute = "/drum-loop-transcription";

export interface TranscriptionModelRequestBody {
    fileKey: string; // S3 key of the audio to transcribe
}

export const isTranscriptionModelRequestBody = (
    value: unknown,
): value is TranscriptionModelRequestBody => isObject(value) && typeof value.fileKey === "string";

export interface TranscribedNote {
    pitch: number; // MIDI note number
    startSeconds: number;
    durationSeconds: number;
    velocity_0to1: number;
}

export interface TranscriptionModelSuccessResponse {
    ok: true;
    notes: TranscribedNote[]; // sorted by start, then pitch
    midiDownloadUrl: string; // presigned URL for the model's .mid file
    bpm?: number; // the tempo the model heard, when it reported one
    key?: string; // the key the model heard, when it reported one (SheetSage2)
}

const isTranscribedNote = (value: unknown): value is TranscribedNote =>
    isObject<TranscribedNote>(value) &&
    typeof value.pitch === "number" &&
    typeof value.startSeconds === "number" &&
    typeof value.durationSeconds === "number" &&
    typeof value.velocity_0to1 === "number";

export const isTranscriptionModelSuccessResponse = (
    value: unknown,
): value is TranscriptionModelSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.notes) &&
    value.notes.every(isTranscribedNote) &&
    typeof value.midiDownloadUrl === "string" &&
    (value.bpm === undefined || typeof value.bpm === "number") &&
    (value.key === undefined || typeof value.key === "string");

export type TranscriptionModelEndpoint = ApiEndpoint<
    TranscriptionModelRequestBody,
    TranscriptionModelSuccessResponse
>;

export const kMuscriptorEndpoint: TranscriptionModelEndpoint = {
    route: kMuscriptorRoute,
    label: "MuScriptor",
    source: "Modal",
    isEvaluationOnly: true,
    category: "Transcription",
    description:
        "Transcribes a recording of any instruments to MIDI with MuScriptor, an open multi-instrument model. Non-commercial weights.",
    isRequestBody: isTranscriptionModelRequestBody,
    isSuccessResponse: isTranscriptionModelSuccessResponse,
};

export const kSheetSage2Endpoint: TranscriptionModelEndpoint = {
    route: kSheetSage2Route,
    label: "SheetSage2",
    source: "Modal",
    isEvaluationOnly: true,
    category: "Transcription",
    description:
        "Transcribes a recording to a lead sheet with SheetSage2: the melody and chords as MIDI, plus the key and tempo. Non-commercial weights.",
    isRequestBody: isTranscriptionModelRequestBody,
    isSuccessResponse: isTranscriptionModelSuccessResponse,
};

export const kDrumLoopTranscriptionEndpoint: TranscriptionModelEndpoint = {
    route: kDrumLoopTranscriptionRoute,
    label: "Drum Loop Deconstruction",
    source: "Modal",
    category: "Transcription",
    description:
        "Transcribes a drum loop to MIDI, one note pitch per drum voice, with the drum loop deconstruction model.",
    isRequestBody: isTranscriptionModelRequestBody,
    isSuccessResponse: isTranscriptionModelSuccessResponse,
};

/** The transcription models served from Modal. Basic Pitch runs in the browser and is separate. */
export const kRemoteTranscriptionModels = ["muscriptor", "sheetsage2", "drum-loop"] as const;
export type RemoteTranscriptionModel = (typeof kRemoteTranscriptionModels)[number];

export const kRemoteTranscriptionEndpoints: Record<
    RemoteTranscriptionModel,
    TranscriptionModelEndpoint
> = {
    muscriptor: kMuscriptorEndpoint,
    sheetsage2: kSheetSage2Endpoint,
    "drum-loop": kDrumLoopTranscriptionEndpoint,
};

/** The longest drum loop the drum model is sent. It returns every stem, so length is memory. */
export const kMaxDrumLoopDuration_s = 30;

/** Why a drum loop of this length is refused, or undefined when it is short enough. */
export const getDrumLoopLengthError = (duration_s: number): string | undefined =>
    duration_s > kMaxDrumLoopDuration_s
        ? `Drum loops can be at most ${String(kMaxDrumLoopDuration_s)} seconds; this one is ${String(Math.round(duration_s))}. Trim it and try again.`
        : undefined;
